import React, { useEffect, useRef, useMemo } from 'react';
import { View, StyleSheet, ActivityIndicator, Animated, Easing, useWindowDimensions } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ExploreFavoritesEmptyState from '@/components/Placeholders/ExploreFavoritesEmptyState';
import {
  EXPLORE_MASONRY_CARD_GAP,
  getExploreMasonryColumnCount,
  getExploreMasonryColumnWidth,
  EXPLORE_LIST_FEED_ASPECT,
} from './exploreMasonryLayout';

const SKELETON_HEIGHTS = [180, 220, 160, 240, 200, 170, 210, 190, 230, 150];

const ShimmerCard = React.memo(({ width, height, delay, colors, rounded = false }) => {
  const shimmer = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(shimmer, {
          toValue: 0.6,
          duration: 900,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(shimmer, {
          toValue: 0.3,
          duration: 900,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    anim.start();
    return () => anim.stop();
  }, [shimmer, delay]);

  return (
    <Animated.View
      style={[
        styles.skeletonCard,
        rounded && styles.skeletonCardFeed,
        {
          width,
          height,
          backgroundColor: colors.solid_color_tertiary,
          opacity: shimmer,
        },
      ]}
    />
  );
});

const ExploreEmptyState = React.memo(({
  isLoading,
  error,
  /** When set (from `ExploreFeed`), skeleton columns match masonry body width. */
  bodyWidth,
  viewMode = 'grid',
  filterFavorites = false,
  onBrowseExplore,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();

  const scrollWidth = bodyWidth ?? windowWidth;

  const numColumns = useMemo(
    () => getExploreMasonryColumnCount(scrollWidth, viewMode),
    [scrollWidth, viewMode],
  );

  const cardWidth = useMemo(
    () => getExploreMasonryColumnWidth(scrollWidth, numColumns, EXPLORE_MASONRY_CARD_GAP, viewMode),
    [scrollWidth, numColumns, viewMode],
  );

  const isListFeed = viewMode === 'list';

  if (isLoading) {
    const skeletonCount = numColumns * 3;
    return (
      <View style={styles.container}>
        <View style={[styles.skeletonGrid, isListFeed && styles.skeletonGridFeed]}>
          {[...Array(skeletonCount)].map((_, i) => {
            const ar = isListFeed ? EXPLORE_LIST_FEED_ASPECT : (SKELETON_HEIGHTS[i % SKELETON_HEIGHTS.length] / cardWidth);
            const height = Math.round(cardWidth / ar);
            return (
              <ShimmerCard
                key={i}
                width={cardWidth}
                height={height}
                delay={i * 80}
                colors={colors}
                rounded={isListFeed}
              />
            );
          })}
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Icon name="circle-exclamation" size={32} color={colors.color_destructive} />
        <Text size="s" color="secondary" style={styles.message}>
          {error}
        </Text>
      </View>
    );
  }

  if (filterFavorites) {
    return (
      <ExploreFavoritesEmptyState onBrowseExplore={onBrowseExplore} />
    );
  }

  return (
    <View style={styles.centered}>
      <Icon name="compass" size={40} color={colors.text_color_tertiary} />
      <Text size="m" weight="semiBold" style={styles.title}>
        {t('explore.noItems')}
      </Text>
      <Text size="s" color="secondary" style={styles.message}>
        {t('explore.noItemsSubtitle')}
      </Text>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingVertical: 12,
    // Horizontal inset comes from `CatalogBrowseShell` / parent; match masonry width.
    paddingHorizontal: 0,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    gap: 10,
  },
  title: {
    marginTop: 4,
  },
  message: {
    textAlign: 'center',
    maxWidth: 280,
  },
  skeletonGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: EXPLORE_MASONRY_CARD_GAP,
  },
  skeletonGridFeed: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 12,
  },
  skeletonCard: {
    borderRadius: 4,
  },
  skeletonCardFeed: {
    borderRadius: 10,
  },
});

ShimmerCard.displayName = 'ShimmerCard';

export default ExploreEmptyState;
